import type { AvatarConfig } from '../avatar/types'

/** Hard cap of one bracket. Ops cannot publish more seats than this. */
export const TOURNAMENT_CAPACITY = 16

/** Smallest field ops can publish. Below this the event is cancelled. */
export const TOURNAMENT_CAPACITY_MIN = 4

/** Seats copied onto the next automatic tournament. */
export const TOURNAMENT_CAPACITY_DEFAULT = 8

/** Below this the event is cancelled and nobody is paid. */
export const TOURNAMENT_MIN_PLAYERS = 4

/**
 * Minutes between automatic starts. 180 lands on 00:00, 03:00, 06:00…
 * Bogotá. Ops can set 30–1440.
 */
export const TOURNAMENT_INTERVAL_DEFAULT_MINUTES = 180
export const TOURNAMENT_INTERVAL_MIN_MINUTES = 30
export const TOURNAMENT_INTERVAL_MAX_MINUTES = 24 * 60

/** Sign-up opens this long before the published start. Same window as Omega. */
export const TOURNAMENT_REGISTER_MS = 30 * 60 * 1000

/** New sign-ups stop this long before the published start. */
export const TOURNAMENT_JOIN_CUTOFF_MS = 30 * 1000

/**
 * The deck dialog submits when the cutoff hits. This extra window lets that
 * request arrive. The sign-up button is already gone.
 */
export const TOURNAMENT_JOIN_GRACE_MS = 5 * 1000

/** How long a seated player has to open the duel before the seat is a loss. */
export const TOURNAMENT_READY_MS = 5 * 60 * 1000

/**
 * Official round length. The 5-minute ready window sits inside this clock.
 * It starts when the round is paired, for every duel in that round.
 */
export const TOURNAMENT_ROUND_MS = 40 * 60 * 1000

export const TOURNAMENT_HISTORY_LIMIT = 10

/** Public hall of fame on the history page. First, second, and third. */
export const TOURNAMENT_PODIUM_SIZE = 3

/**
 * The old fixed hours. An interval of 180 minutes from Bogotá midnight
 * lands on these. The clock reads the saved interval instead.
 */
export const TOURNAMENT_SLOT_HOURS = [0, 3, 6, 9, 12, 15, 18, 21] as const

export const TOURNAMENT_CHAMPION_COINS = 40
export const TOURNAMENT_FINALIST_COINS = 30
export const TOURNAMENT_SEMI_COINS = 20
/**
 * Added to semi, final, and champion for each bracket round past the first two.
 * A player who drops before the semifinal earns this many coins times the round
 * they played, counting from 1.
 */
export const TOURNAMENT_ROUND_BONUS_COINS = 10
/** One Master Pack for anyone who finished a duel. */
export const TOURNAMENT_PARTICIPATION_PACKS = 1

export type TournamentMailPlace =
  | 'champion'
  | 'finalist'
  | 'semi'
  | 'quarters'
  | 'eighths'
  | 'play'

export interface TournamentMailMeta {
  place: TournamentMailPlace
}

export interface TournamentPlayerView {
  userId: string
  username: string
}

export type TournamentMatchStatus = 'waiting' | 'live' | 'done'

/**
 * How a finished bracket match was decided.
 * `checkin` is the 5-minute window: one player opened the room and the other did not.
 */
export type TournamentMatchOutcome = 'play' | 'time' | 'bye' | 'checkin'

/**
 * A duel that was opened. The round clock and a player's clock are time wins.
 * Anything else (life points, deck out, Exodia, surrender) counts as played.
 */
export function tournamentDuelOutcome(reason: string | null | undefined): 'play' | 'time' {
  if (reason === 'timeout' || reason === 'round-time') return 'time'
  return 'play'
}

export interface TournamentMatchView {
  id: string
  round: number
  slot: number
  status: TournamentMatchStatus
  roomCode: string | null
  player0: TournamentPlayerView | null
  player1: TournamentPlayerView | null
  winnerUserId: string | null
  bye: boolean
  /** Why it ended. Absent until the match is done. */
  outcome?: TournamentMatchOutcome | null
  /** Players who never opened the room. The red clock sits on these names. */
  missedUserIds?: string[]
  /** When this round's 40 minutes run out. Absent on a bye, a finished duel, or a preview. */
  endsAt?: string | null
  /** When the 5-minute check-in ends. Set while the match is still waiting. */
  readyEndsAt?: string | null
  /** Staff history only. A played duel keeps this after the public last-10 drops it. */
  replayId?: string | null
}

export type TournamentBannerStatus = 'upcoming' | 'registration' | 'live'

/** Home strip and the top of the history page. No deck lists. */
export interface TournamentPublic {
  status: TournamentBannerStatus
  tournamentId: string | null
  startsAt: string
  opensAt: string
  registered: number
  capacity: number
  entrants: TournamentPlayerView[]
  matches: TournamentMatchView[]
  /** Local preview row. The clock ignores it. */
  sample?: boolean
  /** The 3-hour schedule is stopped. A tournament already open still shows. */
  autosPaused?: boolean
  /**
   * A local preview bracket shown under a real sign-up banner.
   * Absent when the banner itself is the preview, or when a real bracket is live.
   */
  sampleLive?: {
    tournamentId: string | null
    startsAt: string
    matches: TournamentMatchView[]
  } | null
}

export interface TournamentHistoryRow {
  id: string
  startsAt: string
  finishedAt: string
  players: number
  /** Wall clock from the bracket opening to the result. */
  durationMs: number
  champion: TournamentPlayerView | null
  sample?: boolean
}

/** One seat on the all-time tournament podium. */
export interface TournamentPodiumEntry {
  userId: string
  username: string
  avatar: AvatarConfig
  wins: number
}

export interface TournamentDeckView {
  userId: string
  username: string
  main: string[]
  extra: string[]
}

export interface TournamentDetail {
  id: string
  startsAt: string
  finishedAt: string
  /** Wall clock from the bracket opening to the result. */
  durationMs: number
  champion: TournamentPlayerView | null
  matches: TournamentMatchView[]
  decks: TournamentDeckView[]
  sample?: boolean
}

export function tournamentPayout(input: {
  quitFirst: boolean
  finishedDuels: number
  reachedSemi: boolean
  reachedFinal: boolean
  champion: boolean
  /** 0-based round this player was seated in. Ignored from the semifinal on. */
  roundReached: number
  /** Rounds in this bracket. A field of 4 has 2, a field of 16 has 4. */
  bracketRounds: number
}): { coins: number; packs: number; place: TournamentMailPlace | null } {
  const bonus =
    Math.max(0, input.bracketRounds - 2) * TOURNAMENT_ROUND_BONUS_COINS
  if (input.champion) {
    return {
      coins: TOURNAMENT_CHAMPION_COINS + bonus,
      packs: TOURNAMENT_PARTICIPATION_PACKS,
      place: 'champion',
    }
  }
  if (input.quitFirst || input.finishedDuels < 1) {
    return { coins: 0, packs: 0, place: null }
  }
  const packs = TOURNAMENT_PARTICIPATION_PACKS
  if (input.reachedFinal) {
    return { coins: TOURNAMENT_FINALIST_COINS + bonus, packs, place: 'finalist' }
  }
  if (input.reachedSemi) {
    return { coins: TOURNAMENT_SEMI_COINS + bonus, packs, place: 'semi' }
  }
  const round = Math.max(0, input.roundReached)
  return {
    coins: TOURNAMENT_ROUND_BONUS_COINS * (round + 1),
    packs,
    place: input.bracketRounds >= 4 && round === 0 ? 'eighths' : 'quarters',
  }
}
